'use client'

import ArrowRight from '@/components/icons/arrow-right'
import CalendarIcon2 from '@/components/icons/calendar-icon-2'
import CircleWithUpArrow from '@/components/icons/circle-with-up-arrow'
import DollarSign from '@/components/icons/dollar-sign'
import GraphIncrease from '@/components/icons/graph-increase'
import ThreeSliders from '@/components/icons/three-sliders'
import LiveryDesignCTA from '@/components/marketplace/cta/livery-design-cta'
import SearchHeader from '@/components/search/search-header'
import TrackedLink from '@/components/utils/tracking/tracked-link'
import Image from 'next/image'
import Link from 'next/link'
import AutoScroll from './auto-scroll'
import LiveryDropdowns from './livery-dropdowns'

export default function LiveryHomePage({
  searchParams,
  session,
  isLoggedIn,
  trendingSlot,
  games = [],
  platforms = [],
}) {
  return (
    <div className="flex flex-col">
      <AutoScroll searchParams={searchParams} />
      <div className="max-w-(--breakpoint-desktop-lg) relative mx-auto w-full rounded-b-[20px] bg-neutral-blue-800 pb-8 pt-[29px] desktop-xs:rounded-b-[40px] desktop-xs:pb-[48px] desktop-sm:rounded-b-[60px] desktop-sm:py-[75px]">
        <div className="absolute inset-y-0 right-0 hidden h-full w-1/2 overflow-hidden rounded-br-[60px] desktop-sm:block">
          <Image
            src="/img/frontend/homeSearchImg.webp"
            alt="Home Image"
            height={1000}
            width={2560}
            className="relative left-[2px] h-full object-cover"
            priority
          />

          <div className="bg-linear-to-r absolute right-0 top-0 h-full w-full from-neutral-blue-800 via-[#08111B] via-20% to-[#08111bad]" />
          <div className="bg-linear-to-t absolute right-0 top-0 h-full w-full from-neutral-blue-800 to-[#08111b51] to-20%" />
        </div>

        <div className="screen-grid relative gap-5">
          <div className="grid-indent flex flex-col gap-y-8">
            <h2 className="font-inter text-h-3 font-bold desktop-xs:text-h-1 desktop-sm:w-[60%]">
              <span className="gf-gradient-text">Search </span>
              sim racing liveries
            </h2>

            <SearchHeader
              searchParams={searchParams}
              games={games}
              platforms={platforms}
              textPlaceholder="e.g. 1960s Gulf"
              linkPrefix="sim-racing-liveries/search"
              hideDays={true}
              hideCarClass={false}
            />
          </div>
        </div>
      </div>

      {/* margin between header and next comp - done like this as the next comp should change (from trending to featured) */}
      <div className="mt-[52px] desktop-xs:mt-[40px]"></div>

      {trendingSlot}

      <div className="screen-grid mt-[52px] desktop-xs:mt-[74px]">
        <div className="grid-indent flex w-full flex-col items-center">
          <div className="s:block hidden text-[22px] font-bold">
            Enrich your sim racing league experience with
          </div>
          <div className="gf-gradient-text s:block hidden text-[22px] font-bold">
            custom liveries
          </div>

          <span className="s:hidden inline-block items-center text-center text-[22px]">
            Enrich your sim racing league experience with{' '}
            <span className="gf-gradient-text s:hidden inline-block text-[22px] font-bold">
              custom liveries
            </span>
          </span>

          <div className="mt-8 w-[80%] self-center text-center font-normal text-neutral-blue-500 desktop-sm:w-[51%]">
            Whether you are racing in a casual league or competing in a highly
            professional sim racing circuit, a distinctive livery makes you
            stand out on the grid. Grid Finder hosts a diverse range of talented
            designers, ready to create the perfect livery for your sim racing
            adventures.
          </div>

          {!session && (
            <TrackedLink
              data-tracking-event={'Sign up button clicked'}
              trackingdata={{
                type: 'sim-racing-liveries home page',
              }}
              href={`/auth/login?redirectUrl=/sim-racing-liveries&signup=true`}
              className="mx-auto mt-10 flex items-center rounded-sm bg-white p-4 align-middle text-black"
            >
              <span className="font-inter font-semibold">
                Create a free profile
              </span>
            </TrackedLink>
          )}
        </div>
      </div>

      <div className="bg-linear-to-b to-69% mt-[52px] flex w-full flex-col items-center rounded-t-[16px] from-neutral-blue-800 to-neutral-blue-900 px-3 desktop-xs:mt-[58px] desktop-sm:rounded-t-[32px] desktop-md:rounded-t-[60px]">
        <div className="mb-5 mt-[53px] items-center px-4 font-inter font-semibold tracking-[2px] desktop-xs:mt-[107px] desktop-md:mt-28">
          ALL THE TOOLS YOU NEED
        </div>
        <div className="gf-gradient-text items-center px-4 text-center font-syncopate text-[42px] font-bold leading-[37px] desktop-xs:mt-7 desktop-xs:text-[46px] desktop-xs:leading-[48px] desktop-sm:mt-8 desktop-sm:text-[80px] desktop-sm:leading-[78px] desktop-md:mt-6 desktop-md:text-[60px] desktop-md:leading-[56px] desktop-lg:mt-8 desktop-lg:text-[72px] desktop-lg:leading-[70px]">
          WELCOME HOME
        </div>
        <div className="text-hollow items-center px-4 text-center font-syncopate text-[42px] font-bold leading-[40px] desktop-xs:text-[46px] desktop-xs:leading-[50px] desktop-sm:text-[80px] desktop-sm:leading-[78px] desktop-md:text-[60px] desktop-md:leading-[58px] desktop-lg:text-[72px] desktop-lg:leading-[70px]">
          LIVERY DESIGNERS.
        </div>
      </div>

      <div className="screen-grid mt-[20px] desktop-xs:mt-[60px] desktop-sm:mt-[74px]">
        <div className="col-span-full w-full">
          <Image
            src="/img/frontend/liveryHomePageImg.webp"
            alt="An image showing a the livery designer on Grid Finder"
            objectFit="contain"
            height={653}
            width={1200}
            className="mx-auto hidden h-auto w-[90%] rounded-sm desktop-xs:block"
          />
          <Image
            src="/img/frontend/liveryDesignerExample.webp"
            alt="An image showing a the livery designer on Grid Finder"
            objectFit="contain"
            height={653}
            width={1200}
            className="mx-auto h-auto w-[90%] rounded-sm desktop-xs:hidden"
          />
        </div>
        <div className="grid-indent mx-auto mt-[45px] flex w-full justify-between gap-8 font-inter md:gap-0 desktop-xs:mt-[30px] desktop-xs:flex-row">
          <div className="bg-linear-to-br w-[80%] shrink-0 rounded-[12px] from-neutral-blue-600 to-neutral-blue-900 p-px md:w-[23%]">
            <div className="h-full rounded-[12px] bg-neutral-blue-900 p-6 font-inter">
              <DollarSign className="size-12 stroke-primary" />
              <div className="mt-[45px] text-[18px] font-semibold">
                Earn Money. Easily.
              </div>
              <div className="mt-[10px] text-[16px] font-normal text-neutral-blue-500">
                As a livery designer, turn your passion into profit by offering
                your unique designs to a wide audience of dedicated sim racers.
              </div>
            </div>
          </div>
          <div className="bg-linear-to-br w-[80%] shrink-0 rounded-[12px] from-neutral-blue-600 to-neutral-blue-900 p-px md:w-[23%]">
            <div className="h-full rounded-[12px] bg-neutral-blue-900 p-6 font-inter">
              <GraphIncrease className="size-12 stroke-primary" />
              <div className="mt-[45px] text-[18px] font-semibold">
                Analytics Dashboard
              </div>
              <div className="mt-[10px] text-[16px] font-normal text-neutral-blue-500">
                Track your success with our analytics dashboard, understanding
                your audience and refining your designs based on real-time
                feedback.
              </div>
            </div>
          </div>
          <div className="bg-linear-to-br w-[80%] shrink-0 rounded-[12px] from-neutral-blue-600 to-neutral-blue-900 p-px md:w-[23%]">
            <div className="h-full rounded-[12px] bg-neutral-blue-900 p-6 font-inter">
              <ThreeSliders className="size-12 stroke-primary" />
              <div className="mt-[45px] text-[18px] font-semibold">
                Unlimited Uploads
              </div>
              <div className="mt-[10px] text-[16px] font-normal text-neutral-blue-500">
                Enjoy the freedom of expression with unlimited uploads, giving
                you the platform to share all your creative sim racing works of
                art.
              </div>
            </div>
          </div>
          <div className="bg-linear-to-br w-[80%] shrink-0 rounded-[12px] from-neutral-blue-600 to-neutral-blue-900 p-px md:w-[23%]">
            <div className="h-full rounded-[12px] bg-neutral-blue-900 p-6 font-inter">
              <CalendarIcon2 className="size-12 stroke-primary" />
              <div className="mt-[45px] text-[18px] font-semibold">
                Design for Multiple Games
              </div>
              <div className="mt-[10px] text-[16px] font-normal text-neutral-blue-500">
                Expand your reach by designing liveries for multiple sim racing
                games, including ACC, Assetto Corsa, rFactor 2 & iRacing.
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="screen-grid *:grid-indent">
        <ProfileLink isLoggedIn={isLoggedIn} />

        <div className="grid-indent divider my-0 mt-8 h-px desktop-xs:mt-[80px]" />

        <LiveryDesignCTA
          session={session}
          className="mx-auto mt-[62px] w-full rounded-[12px]"
        />

        <BackToTop />

        <div className="grid-indent divider my-0 mt-10 h-px desktop-xs:mt-[100px]" />

        <LiveryDropdowns />
      </div>

      <div className="mt-[72px] desktop-xs:mt-[130px]">
        <LiveryFooter session={session} />
      </div>
    </div>
  )
}

function ProfileLink({ isLoggedIn }) {
  return (
    <Link
      href={
        isLoggedIn
          ? '/profile-manager/liveries/overview'
          : '/profile-manager/add-role'
      }
      className="mx-auto mt-8 flex items-center rounded-sm bg-shades-0 p-4 align-middle text-black transition-all duration-300 ease-in-out hover:bg-shades-0/80 desktop-xs:mt-[57px]"
    >
      <span className="mr-[9px] font-inter font-semibold">
        {isLoggedIn ? 'View your profile' : 'Create a free profile'}
      </span>
      <div className="">
        <ArrowRight className="size-4 stroke-black stroke-2" />
      </div>
    </Link>
  )
}

function BackToTop() {
  return (
    <div
      className="col-span-full mx-auto mt-10 cursor-pointer font-inter text-p-1 font-normal text-neutral-blue-500 desktop-xs:mt-[100px]"
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
    >
      <div className="group flex items-center align-middle">
        <div className="transition-all ease-in-out group-hover:text-white">
          Back to top
        </div>
        <CircleWithUpArrow
          className="ml-1 w-5 stroke-neutral-blue-500 stroke-2 transition-all ease-in-out group-hover:stroke-white"
          stroke="neutral-blue-500"
        />
      </div>
    </div>
  )
}

export function LiveryFooter({ session }) {
  return (
    <div className="relative flex w-full flex-col overflow-hidden rounded-t-lg bg-neutral-blue-800 tablet:rounded-t-[32px] desktop-xs:rounded-t-[32px] desktop-md:rounded-t-[60px]">
      <div className="screen-grid z-2 relative *:col-span-full">
        <div className="mt-8 flex w-full flex-col tablet:flex-row">
          <div className="flex flex-col tablet:w-[60%]">
            <div className="text-hollow-gf-gradient mt-12 font-syncopate text-[36px] font-bold leading-[34px] md:text-[50px] md:leading-[47px] desktop-xs:mt-[130px] desktop-sm:text-[80px] desktop-sm:leading-[73px]">
              MORE THAN JUST RACING
            </div>
            <div className="mt-0 font-syncopate text-[36px] font-bold leading-[34px] text-shades-0 md:text-[50px] md:leading-[47px] desktop-sm:text-[80px] desktop-sm:leading-[73px]">
              THIS IS A PLACE FOR EVERYONE.
            </div>
          </div>

          {!session && (
            <TrackedLink
              data-tracking-event={'Sign up button clicked'}
              trackingdata={{
                type: 'sim-racing-liveries home page',
              }}
              href={`/auth/login?redirectUrl=/sim-racing-liveries&signup=true`}
              className="mb-0 ml-auto mr-0 mt-[30px] flex h-fit w-full items-center rounded-sm bg-shades-0 p-4 align-middle text-black transition-all duration-300 ease-in-out hover:bg-shades-0/80 md:mt-auto md:w-auto"
            >
              <span className="font-inter font-semibold">
                Create a free profile
              </span>
            </TrackedLink>
          )}
        </div>
      </div>

      <div className="wrapper z-2 my-12">
        <div className="footer-marquee font-inter text-[30px]">
          <p className="mr-[8px]">
            League Managers • Drivers • Livery Designers • Commentators •
            Broadcasters • Engineers • League Managers • Drivers • Livery
            Designers • Commentators • Broadcasters • Engineers • League
            Managers • Drivers • Livery Designers • Commentators • Broadcasters
            • Engineers • League Managers • Drivers • Livery Designers •
            Commentators • Broadcasters • Engineers • League Managers • Drivers
            • Livery Designers • Commentators • Broadcasters • Engineers •
            League Managers • Drivers • Livery Designers • Commentators •
            Broadcasters • Engineers • League Managers • Drivers • Livery
            Designers • Commentators • Broadcasters • Engineers • League
            Managers • Drivers • Livery Designers • Commentators • Broadcasters
            • Engineers •
          </p>
        </div>
      </div>

      <div className="screen-grid">
        <div className="divider bg-neutral-blue-700! z-10 col-span-full my-0 mt-8 h-px desktop-xs:mt-[80px]" />
      </div>

      <div className="absolute flex h-full w-full">
        <div className="relative m-auto flex h-full w-full flex-col">
          <div className="relative z-0 h-[calc(100%)] w-[calc(100%)] overflow-hidden rounded-t-[60px]">
            <Image
              src="/img/manager/cars/modal-img.webp"
              alt="A faded image of a car in the background"
              objectFit="cover"
              fill
              className="w-[calc(100%)]! left-0 top-0 h-full rounded-t-[60px] opacity-25"
            />
          </div>
          <div className="absolute flex h-full w-full flex-col md:flex-row">
            <div className="bg-linear-to-b h-full w-full rounded-t-[60px] from-neutral-blue-800 from-5% via-[#08111bb4] via-30% to-neutral-blue-900 to-90%"></div>
          </div>
        </div>
      </div>
    </div>
  )
}
